import { useState } from "react";
import { X, Mail, Phone, CalendarPlus, CalendarX } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/auth";
import { colorFor } from "../shared";

/**
 * Modal that opens when an admin clicks a single event tile in the calendar.
 * Shows the booker's contact details (or "open slot" copy if unbooked) plus
 * a destructive "Remove slot" action.
 */
const EventDetailsModal = ({ event, onClose, onDelete, onCancel }) => {
  const c = colorFor(event);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const bookerEmail = event?.booker?.email;
  const isCancelled = !!event?.booker?.cancelled_at;
  const canInvite = !!event.booked && !!bookerEmail && !!event.slot_id && !isCancelled;
  const canCancel = !!event.booked && !!event.slot_id && !isCancelled;

  const sendInvite = async () => {
    if (!canInvite || sendingInvite) return;
    setSendingInvite(true);
    try {
      const { data } = await api.post(
        `/admin/slots/${event.slot_id}/send-invite`
      );
      toast(
        data?.sent
          ? `Calendar invite sent to ${data.recipient}`
          : `Logged — email service unavailable (${data?.recipient || bookerEmail})`,
        { duration: 3200 }
      );
    } catch (err) {
      toast(err?.response?.data?.detail || "Couldn't send the invite", {
        duration: 2800,
      });
    } finally {
      setSendingInvite(false);
    }
  };

  const cancelAppointment = async () => {
    if (!canCancel || cancelling) return;
    const who = event.booker?.company || event.booker?.name || bookerEmail || "the booker";
    if (
      !window.confirm(
        `Cancel this appointment and email ${who}?\n\nThe slot will stay on the calendar as a held (dark-blue) cell so no one else grabs it.`
      )
    )
      return;
    setCancelling(true);
    try {
      await onCancel(event.slot_id);
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div
      data-testid="event-details-modal"
      className="fixed inset-0 z-[80] bg-black/55 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="relative bg-[var(--ld-ivory)] w-full max-w-md border border-[var(--ld-border)] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          data-testid="event-details-close"
          onClick={onClose}
          className="absolute top-3 right-3 inline-flex h-8 w-8 items-center justify-center text-[var(--ld-ink-muted)] hover:text-[var(--ld-ink)]"
        >
          <X size={16} strokeWidth={1.6} />
        </button>
        <div className={"px-5 py-3 border-b border-[var(--ld-border)] " + c.card}>
          <div className="flex items-center gap-2">
            <p className="font-accent text-[10px] tracking-[0.3em] uppercase">
              {c.label}
            </p>
            {isCancelled && (
              <span
                data-testid="event-cancelled-badge"
                className="inline-flex items-center px-1.5 py-0.5 text-[9px] tracking-[0.22em] uppercase bg-red-800 text-white"
              >
                Cancelled
              </span>
            )}
          </div>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <p className="text-[10px] tracking-[0.22em] uppercase text-[var(--ld-ink-muted)]">
              Date & time
            </p>
            <p className="text-[15px] text-[var(--ld-ink)]">
              {event.date}
              {event.time ? ` · ${event.time}` : ""}
              {event.duration_minutes ? ` · ${event.duration_minutes} min` : ""}
            </p>
          </div>
          {event.booked && event.booker ? (
            <>
              <div className="h-px w-full bg-[var(--ld-border)]" />
              <div>
                <p className="text-[10px] tracking-[0.22em] uppercase text-[var(--ld-ink-muted)]">
                  {event.booker.type === "vendor" ? "Vendor" : "Couple"}
                </p>
                <p
                  data-testid="event-details-name"
                  className="font-serif-display text-xl text-[var(--ld-ink)]"
                >
                  {event.booker.company || event.booker.name || "—"}
                </p>
                {event.booker.company && event.booker.name && (
                  <p className="text-[12px] text-[var(--ld-ink-muted)]">
                    {event.booker.name}
                  </p>
                )}
              </div>
              <div className="grid grid-cols-1 gap-2 pt-1">
                <div className="flex items-center gap-2 text-[14px] text-[var(--ld-ink)]">
                  <Phone size={13} strokeWidth={1.6} />
                  <a
                    data-testid="event-details-phone"
                    href={`tel:${event.booker.phone || ""}`}
                    className="hover:underline"
                  >
                    {event.booker.phone || "—"}
                  </a>
                </div>
                <div className="flex items-center gap-2 text-[14px] text-[var(--ld-ink)]">
                  <Mail size={13} strokeWidth={1.6} />
                  <a
                    data-testid="event-details-email"
                    href={`mailto:${event.booker.email || ""}`}
                    className="hover:underline"
                  >
                    {event.booker.email || "—"}
                  </a>
                </div>
              </div>
            </>
          ) : (
            <p className="text-[13px] text-[var(--ld-ink-muted)] italic">
              This slot is open and ready to book.
            </p>
          )}
          {event.slot_id && (
            <div className="pt-3 flex flex-wrap items-center gap-2">
              {canInvite && (
                <button
                  type="button"
                  onClick={sendInvite}
                  disabled={sendingInvite}
                  data-testid="event-details-send-invite"
                  title={`Email a calendar.ics invite to ${bookerEmail}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase border border-[var(--ld-gold-deep)] text-[var(--ld-gold-deep)] hover:bg-[var(--ld-gold-deep)] hover:text-white transition-colors disabled:opacity-60 disabled:cursor-wait"
                >
                  <CalendarPlus size={12} strokeWidth={1.7} />
                  {sendingInvite ? "Sending…" : "Send invite"}
                </button>
              )}
              {canCancel && (
                <button
                  type="button"
                  onClick={cancelAppointment}
                  disabled={cancelling}
                  data-testid="event-details-cancel"
                  title={`Cancel this appointment and notify ${bookerEmail || "the booker"}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase border border-red-700/60 text-red-800 hover:bg-red-800 hover:text-white transition-colors disabled:opacity-60 disabled:cursor-wait"
                >
                  <CalendarX size={12} strokeWidth={1.7} />
                  {cancelling ? "Cancelling…" : "Cancel appointment"}
                </button>
              )}
              <button
                type="button"
                onClick={() => onDelete(event.slot_id)}
                data-testid="event-details-delete"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase border border-red-500/50 text-red-700 hover:bg-red-500/10"
              >
                Remove slot
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventDetailsModal;
